import RadioGroup from '@mui/material/RadioGroup';
import React, { useState } from 'react';
import { graphql, useFragment } from 'react-relay';

import { useMutationCallbacks } from '@woovi/relay';
import { PAYMENT_METHOD_TYPE } from '@woovi/shared';
import { PayWithPixButton } from '@woovi/ui';

import type { PaymentActiveWaitingPaymentMethod_charge$key } from './__generated__/PaymentActiveWaitingPaymentMethod_charge.graphql.ts';
import type { ChargeAddPaymentMethodMutation } from './mutations/__generated__/ChargeAddPaymentMethodMutation.graphql.ts';
import { ChargeAddPaymentMethod } from './mutations/ChargeAddPaymentMethodMutation.tsx';
import PaymentActiveCustomer from './PaymentActiveCustomer.tsx';
import PaymentActiveMethodInstallment from './PaymentActiveMethodInstallment.tsx';
import PaymentLinkAdditionalInfo from '../paymentLink/PaymentLinkAdditionalInfo.tsx';
import PaymentLinkComment from '../paymentLink/PaymentLinkComment.tsx';
import PaymentLinkPaymentTerm from '../paymentLink/PaymentLinkPaymentTerm.tsx';
import PaymentLinkTransactionDetails from '../PaymentLinkTransactionDetails.tsx';

type PaymentActiveWaitingPaymentMethodProps = {
  charge: PaymentActiveWaitingPaymentMethod_charge$key;
};
const PaymentActiveWaitingPaymentMethod = (
  props: PaymentActiveWaitingPaymentMethodProps,
) => {
  const [installmentNumber, setInstallmentNumber] = useState<string>('1');

  const charge = useFragment<PaymentActiveWaitingPaymentMethod_charge$key>(
    graphql`
      fragment PaymentActiveWaitingPaymentMethod_charge on Charge {
        id
        status
        identifier
        availablePaymentMethods
        company {
          nameFriendly
          taxID {
            taxID
          }
        }
        ...PaymentLinkComment_charge
        ...PaymentActiveCustomer_charge
        ...PaymentActiveMethodInstallment_charge
        ...PaymentLinkAdditionalInfo_charge
        ...PaymentLinkPaymentTerm_charge
        ...PaymentLinkTransactionDetails_charge
      }
    `,
    props.charge,
  );

  const [chargeAddPaymentMethod, isPendingChargeAddPaymentMethod] =
    useMutationCallbacks<ChargeAddPaymentMethodMutation>({
      name: 'ChargeAddPaymentMethod',
      mutation: ChargeAddPaymentMethod,
    });

  const handleChargeAddPaymentMethod = () => {
    if (!installmentNumber) {
      return;
    }

    chargeAddPaymentMethod({
      variables: {
        input: {
          chargeId: charge.id,
          paymentMethodType: PAYMENT_METHOD_TYPE.PIX_CREDIARY,
          paymentMethodInstallmentsConfig: {
            installmentNumber: parseInt(installmentNumber, 10),
          },
        },
      },
    });

    return;
  };

  return (
    <>
      <PaymentLinkComment charge={charge} />
      <PaymentActiveCustomer charge={charge} />
      <RadioGroup
        sx={{ gap: 3 }}
        value={installmentNumber}
        onChange={(e) => {
          setInstallmentNumber(e.target.value);
        }}
      >
        <PaymentActiveMethodInstallment charge={charge} />
      </RadioGroup>

      <PayWithPixButton
        sx={{ width: '100%' }}
        disabled={!installmentNumber}
        onClick={handleChargeAddPaymentMethod}
        isPending={isPendingChargeAddPaymentMethod}
      />
      <PaymentLinkPaymentTerm charge={charge} />
      <PaymentLinkTransactionDetails charge={charge} />
      <PaymentLinkAdditionalInfo charge={charge} />
    </>
  );
};

export default PaymentActiveWaitingPaymentMethod;
